import React, { useState, useMemo } from 'react';
import dayjs from 'dayjs';
import ScrollToTopButton from '@/components/ui/ScrollToTopButton';

interface EventListTableProps {
  events: Array<{
    id: string;
    title: string;
    start: string;
    end: string;
    location?: string;
  }>;
  onEventClick: (eventId: string) => void;
}

const EventListTable: React.FC<EventListTableProps> = ({
  events,
  onEventClick,
}) => {
  const now = dayjs();

  const [startDate, setStartDate] = useState(now.format('YYYY-MM-DD'));
  const [endDate, setEndDate] = useState(
    now.add(1, 'year').format('YYYY-MM-DD')
  );

  const filteredEvents = useMemo(() => {
    return events.filter(event => {
      const eventStart = dayjs(event.start);
      const eventEnd = dayjs(event.end);
      const filterStart = dayjs(startDate);
      const filterEnd = dayjs(endDate);

      return eventStart.isBefore(filterEnd) && eventEnd.isAfter(filterStart);
    });
  }, [events, startDate, endDate]);

  const sortedEvents = filteredEvents.sort((a, b) =>
    dayjs(a.start).diff(dayjs(b.start))
  );

  return (
    <div className='bg-white'>
      <div className='flex flex-nowrap gap-1 mb-4 w-full p-2 bg-[#f5f5f5] rounded-lg'>
        <div className='flex justify-center items-center gap-1'>
          <label className='!text-sm text-[#555]'>Start</label>
          <input
            type='date'
            value={startDate}
            onChange={e => setStartDate(e.target.value)}
            className='p-1 !w-[120px] border border-[#ccc] rounded !text-sm'
          />
        </div>
        <div className='flex justify-center items-center gap-1'>
          <label className='!text-sm text-[#555]'>End</label>
          <input
            type='date'
            value={endDate}
            onChange={e => setEndDate(e.target.value)}
            className='p-1 !w-[120px] border border-[#ccc] rounded !text-sm'
          />
        </div>
      </div>

      <div className='w-full'>
        <table className='w-full border-collapse !mb-40 border-2 border-[#ddd] table-fixed'>
          <thead>
            <tr>
              <th className='p-4 border-b-2 border-r border-[#ddd] bg-[#f5f5f5] w-[25%] !text-sm text-center last:border-r-0'>
                Year
              </th>
              <th className='p-4 border-b-2 border-r border-[#ddd] bg-[#f5f5f5] w-[25%] !text-sm text-center last:border-r-0'>
                Date
              </th>
              <th className='p-4 border-b-2 border-r border-[#ddd] bg-[#f5f5f5] w-[50%] !text-sm text-center last:border-r-0'>
                Event & Location
              </th>
            </tr>
          </thead>
          <tbody>
            {sortedEvents.map(event => (
              <tr
                key={event.id}
                onClick={() => onEventClick(event.id)}
                className='cursor-pointer hover:bg-[#f9f9f9]'
              >
                <td className='p-4 border-b border-r border-[#eee] break-words !text-sm text-center last:border-r-0'>
                  {dayjs(event.start).format('YYYY')}
                </td>
                <td className='p-4 border-b border-r border-[#eee] break-words !text-xs text-center last:border-r-0'>
                  <div className='flex flex-row items-center justify-center gap-1'>
                    <span>{dayjs(event.start).format('MMM DD')}</span>
                    <span>~</span>
                    <span>{dayjs(event.end).format('MMM DD')}</span>
                  </div>
                </td>
                <td className='p-4 border-b border-r border-[#eee] break-words !text-sm text-center last:border-r-0'>
                  <div className='flex flex-col items-center gap-1'>
                    <span className='font-medium'>{event.title}</span>
                    <div className='w-4/5 h-px bg-[#ddd]' />
                    <span className='!text-sm text-[#666]'>
                      {event.location || 'N/A'}
                    </span>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {filteredEvents.length === 0 && (
        <div className='text-center p-8 text-[#888] italic'>
          <h4 className='mb-2'>No events found in the selected date range</h4>
          <p className='!text-sm'>Try adjusting your start and end dates</p>
        </div>
      )}

      <ScrollToTopButton />
    </div>
  );
};

export default EventListTable;
